import * as XLSX from 'xlsx-js-style';
import type { ParsedSpreadsheet } from '../types';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export function parseSpreadsheet(
  file: File,
): Promise<ParsedSpreadsheet> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      reject(
        new Error(
          `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 10 MB.`,
        ),
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetNames = workbook.SheetNames;
        const selected = sheetNames[0];

        if (!selected) {
          reject(new Error('Spreadsheet contains no sheets'));
          return;
        }

        const sheet = workbook.Sheets[selected];

        if (!sheet) {
          reject(new Error(`Sheet "${selected}" not found`));
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(
          sheet,
          {
            defval: '',
            raw: false,
          },
        );

        // Convert all values to strings, filter empty rows
        const rows = jsonData
          .map((row: any) => {
            const stringRow: Record<string, string> = {};
            for (const [key, val] of Object.entries(row)) {
              stringRow[key] = val == null ? '' : String(val);
            }
            return stringRow;
          })
          .filter((row: any) => Object.values(row).some((v: unknown) => typeof v === 'string' && v.trim() !== ''));

        const headers = rows.length > 0 ? Object.keys(rows[0]!) : [];

        resolve({ headers, rows, sheetNames, selectedSheet: selected });
      } catch (_err) {
        reject(
          new Error(
            'Failed to parse spreadsheet. Please check the file format.',
          ),
        );
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}
